import { HttpClient, HttpParams } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { Router } from '@angular/router';
import { AuthorizeService } from 'src/api-authorization/authorize.service';
import { DOCUMENT } from '@angular/common';

type UploadDocsResponse = {
  message: string;
  vectorStoreId: string;
  fileIds: string[];
  appendCurrent: boolean;
};

type CleanupResponse = {
  message: string;
  vectorStoreId?: string;
  fileIds?: string[];
};

type WorkspaceFileRow = {
  fileId: string;
  originalFileName: string;
  contentType: string;
  sizeBytes: number | null;
  uploadedUtc: string;
};

type WorkspaceFilesResponse = {
  vectorStoreId: string | null;
  files: WorkspaceFileRow[];
};

@Component({
  selector: 'jfriedman-ai-upload',
  templateUrl: './aiupload.component.html',
  styleUrls: ['aiupload.component.scss']
})
export class JFriedmanAiUploadComponent implements OnInit, OnDestroy {
  public busyUploading = false;
  public busyDeleting = false;
  public uploadError = '';
  public deleteError = '';
  public appendCurrent = true;
  public selectedFiles: File[] = [];
  public response: UploadDocsResponse | null = null;
  public cleanupResponse: CleanupResponse | null = null;
  public workspaceFiles: WorkspaceFilesResponse | null = null;
  public busyListingWorkspace = false;
  public workspaceListError = '';

  constructor(
    private http: HttpClient,
    private router: Router,
    private authSvc: AuthorizeService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document
  ) {
    const elm = this.document.getElementById('nav-menu');
    if (elm) this.renderer.addClass(elm, 'hideme');
    const elm2 = this.document.getElementById('app-footer');
    if (elm2) this.renderer.addClass(elm2, 'hideme');
  }

  public ngOnDestroy(): void {
    const elm = this.document.getElementById('nav-menu');
    if (elm) this.renderer.removeClass(elm, 'hideme');
    const elm2 = this.document.getElementById('app-footer');
    if (elm2) this.renderer.removeClass(elm2, 'hideme');
  }

  async ngOnInit(): Promise<void> {
    // Ensure user is authenticated and whitelisted (route also has AuthorizeGuard)
    const token = await this.authSvc.getToken();
    if (!token?.sub) {
      this.router.navigate(['/home']);
      return;
    }

    if (token.sub !== 'acfc2497-cb44-4394-b866-3f546b306ff6'
      && token.sub !== '75188666-0c61-4d26-b4d1-d184befa3afb' // Alec Lubin
      && token.sub !== 'e9665018-defb-45b4-a5d7-aff4e4c0ddcd' // jfriedman
      && token.sub !== '074e9283-ff17-49c1-b6f8-787d11dad581'
      && token.sub !== 'ff08b278-700d-4d51-a589-42034ab6c925' // Steve Cohn
      && token.sub !== 'be539c1c-553f-4b1f-bc4b-9575a5917f32' // Tom Simmons
    ) {
      this.router.navigate(['/home']);
      return;
    }

    this.loadWorkspaceFiles();
  }

  loadWorkspaceFiles(): void {
    this.busyListingWorkspace = true;
    this.workspaceListError = '';
    this.http.get<WorkspaceFilesResponse>('/ai/workspace-files').subscribe(
      (resp) => {
        this.workspaceFiles = resp;
        this.busyListingWorkspace = false;
      },
      (err) => {
        this.workspaceListError =
          'Could not load workspace file list. ' + (err?.error?.message || err?.message || 'Unknown error');
        this.workspaceFiles = null;
        this.busyListingWorkspace = false;
      }
    );
  }

  onFilesSelected(event: any): void {
    const files: FileList | undefined = event?.target?.files;
    if (!files || files.length === 0) return;
    this.addFiles(Array.from(files));
  }

  onFilesDropped(event: DragEvent): void {
    event.preventDefault();
    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) return;
    this.addFiles(Array.from(files));
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
  }

  removeFile(idx: number): void {
    this.selectedFiles.splice(idx, 1);
  }

  clearAll(): void {
    this.selectedFiles = [];
    this.response = null;
    this.cleanupResponse = null;
    this.uploadError = '';
    this.deleteError = '';
  }

  private static isPdf(f: File): boolean {
    return f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf');
  }

  private static isDocx(f: File): boolean {
    return f.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      || f.name.toLowerCase().endsWith('.docx');
  }

  private static isImage(f: File): boolean {
    if (f.type && f.type.startsWith('image/')) return true;
    const n = f.name.toLowerCase();
    return /\.(png|jpe?g|gif|bmp|tiff?|heic|webp)$/.test(n);
  }

  private addFiles(files: File[]): void {
    const allowed = files.filter(f =>
      JFriedmanAiUploadComponent.isPdf(f)
      || JFriedmanAiUploadComponent.isDocx(f)
      || JFriedmanAiUploadComponent.isImage(f));
    if (allowed.length !== files.length) {
      this.uploadError = 'Only PDF, Word (.docx), and image files (PNG, JPEG, etc.) are supported.';
    } else {
      this.uploadError = '';
    }

    const existingKeys = new Set(this.selectedFiles.map(f => `${f.name}|${f.size}|${f.lastModified}`));
    for (const f of allowed) {
      const key = `${f.name}|${f.size}|${f.lastModified}`;
      if (!existingKeys.has(key)) {
        this.selectedFiles.push(f);
        existingKeys.add(key);
      }
    }
  }

  upload(): void {
    if (this.selectedFiles.length === 0) {
      this.uploadError = 'Please select at least one PDF, Word document, or image.';
      return;
    }

    this.busyUploading = true;
    this.uploadError = '';
    this.response = null;
    this.cleanupResponse = null;

    const formData = new FormData();
    for (const f of this.selectedFiles) {
      // Must match backend param name: List<IFormFile> files
      formData.append('files', f, f.name);
    }

    const params = new HttpParams().set('appendCurrent', String(this.appendCurrent));

    this.http.post<UploadDocsResponse>('/upload-docs', formData, { params }).subscribe(
      (resp) => {
        this.response = resp;
        this.busyUploading = false;
        this.loadWorkspaceFiles();
      },
      (err) => {
        this.uploadError = 'Upload failed. ' + (err?.error?.message || err?.message || 'Unknown error');
        this.busyUploading = false;
      }
    );
  }

  formatUploaded(iso: string): string {
    if (!iso) return '';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? iso : d.toLocaleString();
  }

  formatSize(bytes: number | null): string {
    if (bytes == null || bytes < 0) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  }

  deleteWorkspace(): void {
    this.busyDeleting = true;
    this.deleteError = '';
    this.cleanupResponse = null;

    this.http.post<CleanupResponse>('/ai/cleanup-current', {}).subscribe(
      (resp) => {
        this.cleanupResponse = resp;
        this.response = null;
        this.selectedFiles = [];
        this.busyDeleting = false;
        this.loadWorkspaceFiles();
      },
      (err) => {
        this.deleteError = 'Delete failed. ' + (err?.error?.message || err?.message || 'Unknown error');
        this.busyDeleting = false;
      }
    );
  }
}

